// ---------------------------------------------------------------------------
// CountryStep — Searchable country list with flag icons
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import type { StepOption } from '@/api/types';
import { OptionCard } from '@/components/shared/OptionCard';
import { SearchInput } from '@/components/shared/SearchInput';
import { StepHeading } from '@/components/shared/StepHeading';
import { CoverageNudge } from '@/components/shared/CoverageNudge';
import { getLocale, t } from '@/i18n';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import { countryFlagUrl } from '@/utils/format';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './CountryStep.module.css';

interface Country {
  option: StepOption;
  label: string;
}

/**
 * Name each country in the widget language (`option.id` is the ISO code) and
 * sort by that name. Keeps the backend name and order when the runtime lacks
 * `Intl.DisplayNames` (older WebViews) or has no name for a code.
 */
function localizeCountries(options: StepOption[], locale: string): Country[] {
  let names: Intl.DisplayNames | undefined;
  let collator: Intl.Collator | undefined;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
    collator = new Intl.Collator([locale]);
  } catch {
    // Unsupported runtime or locale tag — backend names and order stand.
  }

  const countries = options.map((option) => {
    let label = option.name;
    try {
      const name = names?.of(option.id);
      if (name) label = name;
    } catch {
      // Malformed region code — keep the backend name.
    }
    return { option, label };
  });

  return collator ? countries.sort((a, b) => collator.compare(a.label, b.label)) : countries;
}

export const CountryStep = memo(function CountryStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();
  const previousCountry = (collectedData['country_code'] as string | undefined) ?? '';
  const [query, setQuery] = useState('');
  const locale = getLocale();

  const countries = useMemo(
    () => localizeCountries(options.options, locale),
    [options.options, locale],
  );

  const filtered = useMemo(() => {
    if (!query) return countries;
    const lower = query.toLowerCase();
    return countries.filter(
      ({ option, label }) =>
        label.toLowerCase().includes(lower) || option.name.toLowerCase().includes(lower),
    );
  }, [countries, query]);

  const handleSelect = useCallback(
    (id: string) => {
      if (isSubmitting) return;
      onSubmit({ country_code: id });
    },
    [onSubmit, isSubmitting],
  );

  return (
    <>
      <StepHeading title={t('steps.country.title')} icon={getStepIcon(stepId)} />
      {options.options.length > 6 && (
        <div className={styles['search']}>
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('common.search')}
            autoFocus
          />
        </div>
      )}
      <div className={styles['list']} role="listbox" aria-label={t('steps.country.title')}>
        {filtered.map(({ option: o, label }) => (
          <OptionCard
            key={o.id}
            id={o.id}
            name={label}
            isSelected={o.id === previousCountry}
            onClick={handleSelect}
            compact
            icon={
              <img
                src={countryFlagUrl(o.id)}
                alt=""
                width={24}
                height={18}
                className={styles['flag']}
              />
            }
          />
        ))}
      </div>
      <CoverageNudge coverage={options._coverage} />
    </>
  );
});
